import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useSession, useUserProfile } from "@pontimed/auth-client";
import { providerFullDisplayName } from "@pontimed/types";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { m } from "framer-motion";
import {
  ArrowRight,
  Briefcase,
  Building2,
  Calendar,
  CalendarSync,
  Check,
  ChevronRight,
  Clock,
  Crown,
  FolderHeart,
  MapPin,
  Pill,
  Plus,
  Stethoscope,
  Video,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import type { AppointmentCardData } from "@/components/appointments/appointment-card";
import { isSyntheticEmail } from "@/components/auth/profile-completion";
import { PendingSeatBanner } from "@/components/billing/pending-seat-banner";
import { ReferralNudgeCard } from "@/components/referral/referral-nudge-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { readFindSearch } from "@/lib/hooks/use-find-url-memory";
import { tLocalized } from "@/types/localized-string";

export const Route = createFileRoute("/_authenticated/account/user/dashboard")({
  component: DashboardPage,
});

interface SettingsDataLite {
  providerId: string | null;
}

function DashboardPage() {
  const { user } = useSession();
  const profile = useUserProfile();
  const isPremium = user?.role === "user_plus";
  // Legacy hides the "Add provider profile" shortcut when the user already
  // owns a provider profile (session.user.providerId). The session JWT can be
  // stale after a server-side change, so also check settings-data — same key
  // as the workspace switcher, so the query dedupes against its warm cache.
  const sessionProviderId = (user as { providerId?: string } | null)?.providerId;
  const settingsQ = useQuery({
    queryKey: ["api", "/api/account/user/settings-data"],
    queryFn: () => apiFetch.get<SettingsDataLite>("/api/account/user/settings-data"),
    enabled: !!user,
  });
  const hasProvider = !!(sessionProviderId || settingsQ.data?.providerId);
  const fullName = profile.data
    ? `${profile.data.firstName} ${profile.data.lastName}`.trim()
    : null;
  // The h1 already greets by name — the subtitle carries the account email
  // instead of repeating it (never a phone/OAuth placeholder address).
  const email = profile.data?.email ?? null;
  const subtitle = email && !isSyntheticEmail(email) ? email : null;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
          {fullName ? <Trans>Welcome back, {fullName}</Trans> : <Trans>Welcome back</Trans>}
        </h1>
        {(subtitle || isPremium) && (
          <p className="text-muted-foreground mt-1 flex items-center gap-2 text-sm">
            {subtitle && <span className="truncate">{subtitle}</span>}
            {isPremium && (
              <span className="bg-primary-soft text-secondary-foreground inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium">
                <Crown className="size-3" aria-hidden />
                <Trans>Plus</Trans>
              </span>
            )}
          </p>
        )}
      </header>

      <PendingSeatBanner />

      <QuickActions />

      {/* Only cards backed by real data — the static "Active medication" and
          "Recent activity" placeholders had no data source and read as empty
          records, so they are gone until those features exist. */}
      <div className="grid gap-4 lg:grid-cols-2">
        <NextAppointmentCard />
        <CalendarSyncCard isPremium={isPremium} />
      </div>

      {/* Secondary: the referral nudge sits below the patient's own tasks. */}
      <ReferralNudgeCard variant="banner" />

      <WorkspaceShortcuts hasProvider={hasProvider} />

      <AccountSecurityBanner />
    </div>
  );
}

function QuickActions() {
  const navigate = useNavigate();
  // Find tile → restore the last /find URL from sessionStorage if any,
  // so the user lands on their previous filter state instead of restarting
  // the wizard. Empty cache → declarative wizard params take over.
  const handleFindClick = (e: React.MouseEvent) => {
    const cached = readFindSearch();
    if (cached) {
      e.preventDefault();
      void navigate({ to: "/find", search: cached as never });
    }
  };
  return (
    <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <ActionTile
        to="/find"
        onClick={handleFindClick}
        icon={Stethoscope}
        tone="primary"
        title={<Trans>Find a doctor</Trans>}
        subtitle={<Trans>Search the directory</Trans>}
      />
      <ActionTile
        to="/account/user/appointments"
        icon={Calendar}
        tone="blue"
        title={<Trans>Appointments</Trans>}
        subtitle={<Trans>Upcoming and past visits</Trans>}
      />
      <ActionTile
        to="/account/user/medical-vault"
        search={{ tab: "passport", subtab: "active" }}
        icon={FolderHeart}
        tone="purple"
        title={<Trans>Medical vault</Trans>}
        subtitle={<Trans>Passport, journal, treatments</Trans>}
      />
      <ActionTile
        to="/account/user/medical-vault"
        search={{ tab: "treatments", subtab: "active" }}
        icon={Pill}
        tone="orange"
        title={<Trans>Prescriptions</Trans>}
        subtitle={<Trans>Active medication</Trans>}
      />
    </section>
  );
}

interface ActionTileProps {
  to: string;
  search?: Record<string, string>;
  /** Optional click interceptor — used by the Find tile to restore the
   *  last `/find` URL from sessionStorage before falling back to the
   *  declarative wizard params. */
  onClick?: (e: React.MouseEvent) => void;
  icon: LucideIcon;
  tone: "primary" | "blue" | "purple" | "orange";
  title: React.ReactNode;
  subtitle: React.ReactNode;
}

function ActionTile({ to, search, onClick, icon: Icon, tone, title, subtitle }: ActionTileProps) {
  const toneClass = {
    primary: "bg-primary-soft text-secondary-foreground",
    blue: "bg-info-soft text-info-strong",
    purple: "bg-accent-soft text-accent-strong",
    orange: "bg-warning-soft text-warning-strong",
  }[tone];

  const linkClassName =
    "border-border bg-card hover:border-primary/30 flex flex-col gap-3 rounded-xl border p-4 transition-all hover:shadow-md";
  const inner = (
    <>
      <div className={cn("flex size-10 items-center justify-center rounded-lg", toneClass)}>
        <Icon className="size-5" aria-hidden />
      </div>
      <div>
        <p className="text-foreground text-sm font-medium">{title}</p>
        <p className="text-muted-foreground mt-0.5 text-xs">{subtitle}</p>
      </div>
    </>
  );

  return (
    <m.div whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}>
      {search ? (
        <Link to={to} search={search as never} onClick={onClick} className={linkClassName}>
          {inner}
        </Link>
      ) : (
        <Link to={to} onClick={onClick} className={linkClassName}>
          {inner}
        </Link>
      )}
    </m.div>
  );
}

function SectionCard({
  title,
  icon: Icon,
  iconClassName,
  action,
  children,
}: {
  title: React.ReactNode;
  icon: LucideIcon;
  iconClassName?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between">
          <h2 className="text-foreground flex items-center gap-2 text-sm font-semibold">
            <Icon className={cn("size-4", iconClassName ?? "text-primary")} aria-hidden />
            {title}
          </h2>
          {action}
        </div>
        <div className="mt-4">{children}</div>
      </CardContent>
    </Card>
  );
}

function EmptyState({
  icon: Icon,
  message,
  action,
}: {
  icon: LucideIcon;
  message: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="border-border flex items-center justify-center rounded-lg border border-dashed py-8">
      <div className="text-center">
        <Icon className="text-muted-foreground/40 mx-auto size-8" aria-hidden />
        <p className="text-muted-foreground mt-2 text-sm">{message}</p>
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}

interface UpcomingAppointmentsResponse {
  appointments: AppointmentCardData[];
  total: number;
}

const NEXT_APPOINTMENT_URL = "/api/account/appointments?tab=upcoming&page=1&limit=1";

function NextAppointmentCard() {
  // Same endpoint the Appointments page uses; tab=upcoming is sorted by
  // startsAt asc, so limit=1 is the next visit. Always refetch on mount so a
  // booking made a minute ago shows up instead of a cached empty state.
  const nextQ = useQuery({
    queryKey: ["api", NEXT_APPOINTMENT_URL],
    queryFn: async () => {
      try {
        return await apiFetch.get<UpcomingAppointmentsResponse>(NEXT_APPOINTMENT_URL);
      } catch (err) {
        // 400 = no patient profile linked yet → no appointments, not an error.
        if (err instanceof ApiError && err.status === 400) return { appointments: [], total: 0 };
        throw err;
      }
    },
    staleTime: 0,
    refetchOnMount: true,
  });
  const next = nextQ.data?.appointments?.[0] ?? null;

  return (
    <SectionCard
      title={<Trans>Next appointment</Trans>}
      icon={Calendar}
      action={
        <Link
          to="/account/user/appointments"
          search={{ tab: "upcoming" }}
          className="text-primary inline-flex min-h-11 items-center text-xs hover:underline"
        >
          <Trans>View all</Trans>
        </Link>
      }
    >
      {nextQ.isPending ? (
        <div className="flex items-center gap-3 rounded-lg border border-transparent p-3">
          <Skeleton className="h-14 w-14 shrink-0 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ) : nextQ.isError ? (
        <div className="border-border flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-6 text-center">
          <p className="text-muted-foreground text-sm">
            <Trans>Couldn't load appointments. Please try again.</Trans>
          </p>
          <Button
            size="sm"
            variant="outline"
            className="min-h-11"
            onClick={() => void nextQ.refetch()}
          >
            <Trans>Retry</Trans>
          </Button>
        </div>
      ) : next ? (
        <NextAppointmentSummary appointment={next} />
      ) : (
        <EmptyState
          icon={Clock}
          message={<Trans>No upcoming appointment</Trans>}
          action={
            <Link
              to="/find"
              className="text-primary inline-flex min-h-11 items-center gap-1 text-xs font-medium hover:underline"
            >
              <Plus className="size-3" aria-hidden />
              <Trans>Book now</Trans>
            </Link>
          }
        />
      )}
    </SectionCard>
  );
}

function NextAppointmentSummary({ appointment: apt }: { appointment: AppointmentCardData }) {
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const start = new Date(apt.startsAt);
  const dayName = start.toLocaleDateString(locale, { weekday: "short" });
  const month = start.toLocaleDateString(locale, { month: "short" });
  const time = start.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  const providerName = providerFullDisplayName(apt.provider, locale);
  const specialty = apt.provider.specialty ? tLocalized(apt.provider.specialty.name, locale) : "";
  const facilityName = apt.facility ? tLocalized(apt.facility.name, locale) : "";
  const isVideo = apt.type === "video";

  return (
    <Link
      to="/account/user/appointments"
      search={{ tab: "upcoming" }}
      className="border-border hover:border-primary/30 flex items-center gap-3 rounded-lg border p-3 transition-colors"
    >
      <div className="bg-primary-soft flex w-14 shrink-0 flex-col items-center justify-center rounded-lg py-2">
        <span className="text-secondary-foreground text-[0.625rem] font-medium uppercase">
          {dayName}
        </span>
        <span className="text-foreground text-xl leading-tight font-bold">{start.getDate()}</span>
        <span className="text-secondary-foreground text-[0.625rem]">{month}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-foreground truncate text-sm font-semibold">{providerName}</p>
        {specialty && <p className="text-muted-foreground truncate text-xs">{specialty}</p>}
        <p className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
          <span className="inline-flex items-center gap-1">
            <Clock className="size-3" aria-hidden />
            {time}
          </span>
          {isVideo ? (
            <span className="inline-flex items-center gap-1">
              <Video className="size-3" aria-hidden />
              <Trans>Video</Trans>
            </span>
          ) : facilityName ? (
            <span className="inline-flex min-w-0 items-center gap-1">
              <MapPin className="size-3 shrink-0" aria-hidden />
              <span className="truncate">{facilityName}</span>
            </span>
          ) : null}
        </p>
      </div>
      <ChevronRight className="text-muted-foreground size-4 shrink-0" aria-hidden />
    </Link>
  );
}

function CalendarSyncCard({ isPremium }: { isPremium: boolean }) {
  const { t } = useLingui();
  if (isPremium) {
    return (
      <SectionCard
        title={<Trans>Calendar sync</Trans>}
        icon={CalendarSync}
        action={
          <Link
            to="/account/user/settings"
            search={{ tab: "calendar" }}
            className="text-primary inline-flex min-h-11 items-center text-xs hover:underline"
          >
            <Trans>Manage</Trans>
          </Link>
        }
      >
        <div className="border-border flex items-center gap-3 rounded-lg border p-4">
          <div className="bg-success-soft flex size-10 shrink-0 items-center justify-center rounded-full">
            <Check className="text-success-strong size-5" aria-hidden />
          </div>
          <div className="flex-1">
            <p className="text-foreground text-sm font-medium">
              <Trans>Calendar sync active</Trans>
            </p>
            <p className="text-muted-foreground text-xs">
              <Trans>Visits land on Google / Apple / Outlook automatically.</Trans>
            </p>
          </div>
          <Link
            to="/account/user/settings"
            search={{ tab: "calendar" }}
            aria-label={t`Manage`}
            className="text-primary -m-3 flex size-11 shrink-0 items-center justify-center"
          >
            <ArrowRight className="size-4" aria-hidden />
          </Link>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={<Trans>Calendar sync</Trans>} icon={CalendarSync}>
      <div className="bg-primary/5 border-primary/20 rounded-lg border p-4">
        <div className="flex items-start gap-3">
          <Crown className="text-primary mt-0.5 size-5 shrink-0" aria-hidden />
          <div>
            <p className="text-foreground text-sm font-medium">
              <Trans>Sync your appointments to Google / Apple / Outlook</Trans>
            </p>
            <p className="text-muted-foreground mt-0.5 text-xs">
              <Trans>Available with Plus.</Trans>
            </p>
            <Link
              to="/account/user/billing"
              search={{ tab: "subscription" }}
              className="text-primary mt-1 inline-flex min-h-11 items-center gap-1 text-xs font-medium hover:underline"
            >
              <Trans>Upgrade to Plus</Trans>
              <ArrowRight className="size-3" />
            </Link>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

function WorkspaceShortcuts({ hasProvider }: { hasProvider: boolean }) {
  const navigate = useNavigate();
  const open = (target: "provider" | "facility" | "business") =>
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        add: target,
        aStep: undefined,
        aMode: undefined,
      }),
    });

  return (
    <section className="space-y-2">
      {!hasProvider && (
        <WorkspaceTrigger
          icon={Stethoscope}
          label={<Trans>Add provider profile</Trans>}
          onClick={() => open("provider")}
        />
      )}
      <WorkspaceTrigger
        icon={Building2}
        label={<Trans>Add facility</Trans>}
        onClick={() => open("facility")}
      />
      <WorkspaceTrigger
        icon={Briefcase}
        label={<Trans>Add business</Trans>}
        onClick={() => open("business")}
      />
    </section>
  );
}

function WorkspaceTrigger({
  icon: Icon,
  label,
  onClick,
}: {
  icon: LucideIcon;
  label: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border-border bg-card hover:border-primary/30 flex min-h-11 w-full items-center gap-3.5 rounded-lg border px-4 py-3.5 text-left transition-colors"
    >
      <Icon className="text-primary size-5" aria-hidden />
      <span className="text-foreground text-[0.9375rem]">{label}</span>
    </button>
  );
}
